import type { ReactNode } from 'react';
import { memo, useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { createId } from '@paralleldrive/cuid2';
import { ChevronRight } from 'react-feather';
import { useIsFullPageChat } from '@/components/Chat/hooks';
import { ChatStateDispatchContext } from '@/components/Chat.State/context';
import { KolSearchStatus } from '@/enums/kol';
import { useIsDemoGroup } from '@/store/selectors';
import { messageLaneRightPadding, messageLaneWidth, messageLaneLeftPadding } from './Message.styles';
import styles from './style.module.css';

export const KolContextGetStarted = memo(() => {

  const dispatch = useContext(ChatStateDispatchContext);
  const isDemoGroup = useIsDemoGroup();

  const handleSearchClick = useCallback(() => {
    dispatch({
      type: 'kol-search-added',
      payload: {
        item: {
          id: null,
          createdOn: new Date(),
          status: KolSearchStatus.FormEntry,
          identifier: createId(),
          followupQuestions: [],
          form: {
            name: '',
          },
          results: null,
          profile: null,
          recommendations: {
            status: 'skipped',
            items: [],
            total: 0,
          },
          citations: [],
        },
      },
    });
  }, [dispatch]);

  const handleRecommendationsClick = useCallback(() => {
    dispatch({
      type: 'kol-search-added',
      payload: {
        item: {
          id: null,
          createdOn: new Date(),
          status: KolSearchStatus.ProfileRecommendations,
          identifier: createId(),
          followupQuestions: [],
          form: {
            name: '',
          },
          results: null,
          profile: null,
          recommendations: {
            status: 'uninitialized',
            items: [],
            total: 0,
          },
          citations: [],
        },
      },
    });
  }, [dispatch]);

  const handleQuestionBankClick = useCallback(() => {
    dispatch({
      type: 'kol-search-added',
      payload: {
        item: {
          id: null,
          createdOn: new Date(),
          status: KolSearchStatus.QuestionsBank,
          identifier: createId(),
          followupQuestions: [],
          form: {
            name: '',
          },
          results: null,
          profile: null,
          recommendations: {
            status: 'skipped',
            items: [],
            total: 0,
          },
          citations: [],
        },
      },
    });
  }, [dispatch]);

  const items: GetStartedCardProps[] = useMemo(() => [
    {
      title: 'HCP Insights',
      copy: <>Find HCPs and pull insights on their attributes, previous engagements, and medical experience</>,
      icon: <StyledRecommendationsIcon />,
      onClick: handleSearchClick,
    },
    {
      title: isDemoGroup
        ? 'Call Plan & Recommendations'
        : 'Territory Level Insights & Medical Unmet Needs Recommendations',
      copy: isDemoGroup
        ? 'Explore recommended HCPs to engage in your territory'
        : 'Explore recommended HCPs to engage in your territory based on prioritized Medical Unmet Needs targets',
      icon: <StyledSearchIcon />,
      onClick: handleRecommendationsClick,
    },
    {
      title: 'Question Bank',
      copy: <>Here is everything that you can ask me</>,
      icon: <StyledQuestionBankIcon />,
      onClick: handleQuestionBankClick,
    },
  ], [handleSearchClick, handleRecommendationsClick, handleQuestionBankClick, isDemoGroup]);

  const isFullPageChat = useIsFullPageChat();

  return (
    <Root className={!isFullPageChat ? styles.cards : undefined}>
      {items.map(({ copy, icon, onClick, title }, index) => (
        <GetStartedCard
          key={index}
          copy={copy}
          icon={icon}
          onClick={onClick}
          title={title} />
      ))}
    </Root>
  );
});

const Root = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '100%',
  width: messageLaneWidth,
  margin: '0 auto',
  padding: `0 ${messageLaneRightPadding}px 0 ${messageLaneLeftPadding}px`,
});

type GetStartedCardProps = {
  copy: ReactNode;
  icon: ReactNode;
  onClick: () => void;
  title: ReactNode;
};

const GetStartedCard = memo(({ copy, icon, onClick, title }: GetStartedCardProps) => {
  const isFullPageChat = useIsFullPageChat();
  return (
    <CardRoot
      className={!isFullPageChat ? styles.card : undefined}
      onClick={onClick}>
      <div>
        {icon}
        <CardTitle className={!isFullPageChat ? styles.title : undefined}>{title}</CardTitle>
        <CardCopy>{copy}</CardCopy>
      </div>

      <CardFooter>
        <CardLink>
          <StyledChevronRightIcon size={24} />
        </CardLink>
      </CardFooter>
    </CardRoot>
  );
});

type IconProps = {
  className?: string;
};

const SearchIcon = memo(({ className }: IconProps) => {

  return (
    <svg
      className={className}
      x="0px"
      y="0px"
      viewBox="0 0 512 512"
      // style={{ enableBackground: 'new 0 0 512 512' }}
      xmlSpace="preserve">
      <g>
        <g>
          <path d="M462,44h-47.667V30c0-16.542-13.458-30-30-30s-30,13.458-30,30v14h-168V30c0-16.542-13.458-30-30-30s-30,13.458-30,30
          v14.37c-0.85-0.235-1.741-0.37-2.666-0.37H83.823c-27.57,0-50,22.43-50,50v170.333c0,5.523,4.478,10,10,10s10-4.477,10-10V147
          h360.51c5.522,0,10-4.477,10-10s-4.478-10-10-10H53.823V94c0-16.542,13.458-30,30-30h39.844c0.925,0,1.816-0.136,2.666-0.37V76.5
          c0,16.542,13.458,30,30,30s30-13.458,30-30V64h168v12.5c0,16.542,13.458,30,30,30s30-13.458,30-30V64H462
          c16.542,0,30,13.458,30,30v379.994c0,9.928-8.077,18.006-18.006,18.006s-18.006-8.078-18.006-18.006v-25.869
          c0-5.523-4.478-10-10-10H329.667c-5.522,0-10,4.477-10,10s4.478,10,10,10h106.321v15.869c0,6.511,1.648,12.643,4.545,18.006H38
          c-9.925,0-18-8.075-18-18v-15.875h223.825c5.522,0,10-4.477,10-10s-4.478-10-10-10h-99.916
          c17.474-15.049,28.57-37.309,28.57-62.125c0-45.215-36.785-82-82-82c-45.215,0-82,36.785-82,82
          c0,24.816,11.096,47.076,28.57,62.125H10c-5.522,0-10,4.477-10,10V474c0,20.953,17.047,38,38,38h435.994
          C494.95,512,512,494.951,512,473.994V94C512,66.43,489.57,44,462,44z M166.333,76.5c0,5.514-4.486,10-10,10s-10-4.486-10-10V30
          c0-5.514,4.486-10,10-10s10,4.486,10,10V76.5z M394.333,76.5c0,5.514-4.486,10-10,10c-5.514,0-10-4.486-10-10V30
          c0-5.514,4.486-10,10-10c5.514,0,10,4.486,10,10V76.5z M28.479,376c0-34.187,27.813-62,62-62s62,27.813,62,62s-27.813,62-62,62
          S28.479,410.187,28.479,376z" />
        </g>
      </g>
      <g>
        <g>
          <path d="M468.309,129.93c-1.859-1.86-4.439-2.93-7.069-2.93c-2.631,0-5.21,1.07-7.07,2.93c-1.86,1.86-2.93,4.44-2.93,7.07
          s1.069,5.21,2.93,7.07c1.861,1.86,4.439,2.93,7.07,2.93c2.63,0,5.21-1.07,7.069-2.93c1.86-1.86,2.931-4.44,2.931-7.07
          S470.17,131.79,468.309,129.93z" />
        </g>
      </g>
      <g>
        <g>
          <path d="M298.649,441.05c-1.859-1.86-4.439-2.92-7.069-2.92s-5.21,1.06-7.07,2.92c-1.86,1.87-2.93,4.44-2.93,7.07
          c0,2.64,1.069,5.21,2.93,7.08c1.86,1.86,4.44,2.92,7.07,2.92s5.21-1.06,7.069-2.92c1.86-1.87,2.931-4.45,2.931-7.08
          C301.58,445.49,300.51,442.92,298.649,441.05z" />
        </g>
      </g>
      <g>
        <g>
          <path d="M226.245,304c-20.953,0-38,17.047-38,38s17.047,38,38,38s38-17.047,38-38S247.198,304,226.245,304z M226.245,360
          c-9.925,0-18-8.075-18-18s8.075-18,18-18s18,8.075,18,18S236.17,360,226.245,360z" />
        </g>
      </g>
      <g>
        <g>
          <path d="M319.578,304c-20.953,0-38,17.047-38,38s17.047,38,38,38s38-17.047,38-38S340.531,304,319.578,304z M319.578,360
          c-9.925,0-18-8.075-18-18s8.075-18,18-18s18,8.075,18,18S329.503,360,319.578,360z" />
        </g>
      </g>
      <g>
        <g>
          <path d="M412.912,304c-20.953,0-38,17.047-38,38s17.047,38,38,38c20.953,0,38-17.047,38-38S433.865,304,412.912,304z M412.912,360
          c-9.925,0-18-8.075-18-18s8.075-18,18-18s18,8.075,18,18S422.837,360,412.912,360z" />
        </g>
      </g>
      <g>
        <g>
          <path d="M132.912,200c-20.953,0-38,17.047-38,38s17.047,38,38,38s38-17.047,38-38S153.865,200,132.912,200z M132.912,256
          c-9.925,0-18-8.075-18-18s8.075-18,18-18c9.925,0,18,8.075,18,18S142.837,256,132.912,256z" />
        </g>
      </g>
      <g>
        <g>
          <path d="M319.578,200c-20.953,0-38,17.047-38,38s17.047,38,38,38s38-17.047,38-38S340.531,200,319.578,200z M319.578,256
          c-9.925,0-18-8.075-18-18s8.075-18,18-18s18,8.075,18,18S329.503,256,319.578,256z" />
        </g>
      </g>
      <g>
        <g>
          <path d="M412.912,200c-20.953,0-38,17.047-38,38s17.047,38,38,38c20.953,0,38-17.047,38-38S433.865,200,412.912,200z M412.912,256
          c-9.925,0-18-8.075-18-18s8.075-18,18-18s18,8.075,18,18S422.837,256,412.912,256z" />
        </g>
      </g>
      <g>
        <g>
          <path d="M226.245,200c-20.953,0-38,17.047-38,38s17.047,38,38,38s38-17.047,38-38S247.198,200,226.245,200z M226.245,256
          c-9.925,0-18-8.075-18-18s8.075-18,18-18s18,8.075,18,18S236.17,256,226.245,256z" />
        </g>
      </g>
      <g>
        <g>
          <path d="M126.104,351.629c-3.906-3.905-10.236-3.905-14.143,0l-32.566,32.567l-11.129-11.129c-3.906-3.905-10.236-3.905-14.143,0
          c-3.905,3.905-3.905,10.237,0,14.143l18.201,18.199c1.876,1.875,4.419,2.929,7.071,2.929c2.652,0,5.195-1.054,7.071-2.929
          l39.638-39.638C130.009,361.866,130.009,355.534,126.104,351.629z" />
        </g>
      </g>
    </svg>
  );
});

const RecommendationsIcon = memo(({ className }: IconProps) => {

  return (
    <svg
      className={className}
      viewBox="0 0 682.7 682.7">
      <path d="M515.9,521.3l-97.8-41.9c-8.1-3.5-13.4-11.4-13.4-20.3v-39.3c35.6-20.5,60.4-57.6,63.7-100.6h6.4c10.4,0,20.3-4.4,27.2-12.1,6.9-7.7,10.3-18,9.3-28.4-.8-8.4-4.8-16-10.6-21.7,0-.4,0-.8,0-1.3v-32.1c0-52.2-25.2-98.7-64.1-127.9,0-.1,0-.3,0-.4,0-52.7-42.8-95.5-95.5-95.5s-95.5,42.8-95.5,95.5,0,.2,0,.3c-4.7,3.5-9.2,7.3-13.6,11.3-29.5,27.6-47.3,65-50.2,105.2-.4,5.5,3.8,10.3,9.3,10.7,5.5.4,10.3-3.8,10.7-9.3,2.5-35.2,18.1-67.8,43.9-92,25.9-24.3,59.8-37.7,95.4-37.7,77,0,139.6,62.6,139.6,139.6v22.8c-2.5-.5-5-.7-7.6-.7h-14.5c-6.3,0-11.4-5.1-11.4-11.4v-7.1c0-21.6-12.2-40.4-30.1-49.9,9.2-5.5,14.5-9.2,15.3-9.7,4.5-3.2,5.5-9.5,2.3-13.9-3.2-4.5-9.5-5.5-14-2.3-.7.5-69,48.6-166.6,61.1-11,1.4-19.3,10.8-19.3,21.8s-5.1,11.4-11.4,11.4h-16c-10.4,0-20.3,4.4-27.2,12.1-6.9,7.7-10.3,18-9.3,28.4,1.8,18.5,18.6,33,38.1,33h4.9c2.3,30.3,15.3,58.5,37,80.2,8.1,8.1,17.1,15,26.8,20.5v39.2c0,8.8-5.3,16.8-13.4,20.3l-97.8,41.9c-23.4,10-38.5,32.9-38.5,58.3v93c0,5.5,4.5,10,10,10s10-4.5,10-10v-93c0-17.4,10.3-33.1,26.3-39.9l60.5-25.9v63.4h-32.8c-17.3,0-31.4,14.1-31.4,31.4v42.8c0,17.3,14.1,31.4,31.4,31.4s10-4.5,10-10-4.5-10-10-10-11.4-5.1-11.4-11.4v-42.8c0-6.3,5.1-11.4,11.4-11.4h85.5c6.3,0,11.4,5.1,11.4,11.4v42.8c0,6.3-5.1,11.4-11.4,11.4s-10,4.5-10,10,4.5,10,10,10c17.3,0,31.4-14.1,31.4-31.4v-42.8c0-17.3-14.1-31.4-31.4-31.4h-32.8v-56.8l68.8,45.9c5.3,3.5,11.3,5.3,17.4,5.3s12.1-1.8,17.4-5.3l68.8-45.9v79.4c-18.4,4.5-32.1,21.1-32.1,40.9s18.9,42.1,42.1,42.1,42.1-18.9,42.1-42.1-13.7-36.4-32.1-40.9v-86l60.5,25.9c16,6.9,26.3,22.5,26.3,39.9v93c0,5.5,4.5,10,10,10s10-4.5,10-10v-93c0-25.4-15.1-48.3-38.5-58.3ZM267,82.5c6.2-35.4,37.2-62.5,74.4-62.5s68.2,27.1,74.4,62.5c-22.2-11.8-47.5-18.4-74.4-18.4s-51.6,6.4-74.4,18.3ZM233.8,309.3c0-2.7-1.1-5.2-2.9-7.1s-4.4-2.9-7.1-2.9h-14.5c-9.3,0-17.3-6.6-18.2-15-.5-4.8,1-9.4,4.2-13,3.2-3.5,7.7-5.5,12.4-5.5h16c17.3,0,31.4-14.1,31.4-31.4s.8-1.8,1.9-1.9c57.3-7.3,104.4-26,135.4-41.4,19.5.7,35.1,16.7,35.1,36.4v7.1c0,17.3,14.1,31.4,31.4,31.4h14.5c9.3,0,17.3,6.6,18.2,15,.5,4.8-1,9.4-4.2,13-3.2,3.5-7.7,5.5-12.4,5.5h-16c-5.5,0-10,4.5-10,10,0,59.3-48.3,107.6-107.6,107.6s-55.7-11.2-76.1-31.5c-20.3-20.3-31.5-47.3-31.5-76.1ZM347.6,549.6c-3.8,2.6-8.8,2.6-12.6,0l-71.7-47.8,9.1-3.9c15.5-6.6,25.5-21.8,25.5-38.7v-29.9c13.7,5,28.4,7.6,43.4,7.6s29.9-2.7,43.4-7.6v30c0,16.9,10,32,25.5,38.7l9.1,3.9-71.7,47.8ZM459.6,640.6c0,12.2-9.9,22.1-22.1,22.1s-22.1-9.9-22.1-22.1,9.9-22.1,22.1-22.1,22.1,9.9,22.1,22.1Z" />
      <path d="M287.9,267.2c-5.5,0-10,4.5-10,10v10.7c0,5.5,4.5,10,10,10s10-4.5,10-10v-10.7c0-5.5-4.5-10-10-10Z" />
      <path d="M394.8,297.9c5.5,0,10-4.5,10-10v-10.7c0-5.5-4.5-10-10-10s-10,4.5-10,10v10.7c0,5.5,4.5,10,10,10Z" />
      <path d="M381.1,335c-3.5-4.3-9.8-4.9-14.1-1.4-6.5,5.3-15.8,8.4-25.7,8.4s-19.2-3.1-25.7-8.4c-4.3-3.5-10.6-2.9-14.1,1.4s-2.9,10.6,1.4,14.1c10.1,8.4,23.8,13,38.4,13s28.3-4.6,38.4-13c4.3-3.5,4.9-9.8,1.4-14.1Z" />
      <path d="M661.2,487.3l-42.2-14.1c-.3,0-.5-.4-.5-.7v-27.5c19.4-13.4,32.1-35.7,32.1-61v-32.1c0-29.1-23.7-52.8-52.8-52.8h-42.8c-29.1,0-52.8,23.7-52.8,52.8v32.1c0,25.3,12.7,47.6,32.1,61v27.5c0,.3-.2.6-.5.7l-14.1,4.7c-5.2,1.7-8.1,7.4-6.3,12.6,1.4,4.2,5.3,6.8,9.5,6.8s2.1-.2,3.2-.5l9-3,28.9,21.6c3.7,2.7,8,4.1,12.4,4.1s8.8-1.4,12.4-4.1l20.3-15.3v46.5c-12.4,4.2-21.4,15.9-21.4,29.7s14.1,31.4,31.4,31.4,31.4-14.1,31.4-31.4-9-25.5-21.4-29.7v-49l25.7,8.6c4.7,1.6,7.8,5.9,7.8,10.8v80.8c0,5.5,4.5,10,10,10s10-4.5,10-10v-80.8c0-13.5-8.6-25.5-21.5-29.8ZM522.3,384.1v-32.1c0-18.1,14.7-32.8,32.8-32.8h42.8c18.1,0,32.8,14.7,32.8,32.8v32.1c0,29.8-24.3,54.1-54.1,54.1s-54.1-24.3-54.1-54.1ZM576.9,499.5c-.2.2-.6.2-.8,0l-23.8-17.8c1.4-2.8,2.1-5.9,2.1-9.1v-17.7c7,2.2,14.4,3.4,22.1,3.4s15.1-1.2,22.1-3.4v17.7c0,3.2.8,6.3,2.1,9.1l-23.8,17.8ZM619.2,587.8c-6.3,0-11.4-5.1-11.4-11.4s5.1-11.4,11.4-11.4,11.4,5.1,11.4,11.4-5.1,11.4-11.4,11.4Z" />
      <path d="M93.8,515.5c3.7,2.7,8,4.1,12.4,4.1s8.8-1.4,12.4-4.1l28.9-21.6,9,3c1,.4,2.1.5,3.2.5,4.2,0,8.1-2.7,9.5-6.8,1.7-5.2-1.1-10.9-6.3-12.6l-14.1-4.7c-.3,0-.5-.4-.5-.7v-27.5c19.4-13.4,32.1-35.7,32.1-61v-42.8c0-23.2-18.9-42.1-42.1-42.1h-64.1c-23.2,0-42.1,18.9-42.1,42.1v42.8c0,25.3,12.7,47.6,32.1,61v27.5c0,.3-.2.6-.5.7l-42.2,14.1c-12.8,4.3-21.5,16.2-21.5,29.8v80.8c0,5.5,4.5,10,10,10s10-4.5,10-10v-80.8c0-4.9,3.1-9.2,7.8-10.8l36.3-12.1v50.9h-11.4c-11.4,0-20.7,9.3-20.7,20.7v21.4c0,11.4,9.3,20.7,20.7,20.7s10-4.5,10-10-4.5-10-10-10-.7-.3-.7-.7v-21.4c0-.4.3-.7.7-.7h42.8c.4,0,.7.3.7.7v21.4c0,.4-.3.7-.7.7-5.5,0-10,4.5-10,10s4.5,10,10,10c11.4,0,20.7-9.3,20.7-20.7v-21.4c0-11.4-9.3-20.7-20.7-20.7h-11.4v-36.8l9.7,7.2ZM74.1,319.3h64.1c12.2,0,22.1,9.9,22.1,22.1v21.6c-32.1-2.7-54.6-17.6-63.3-24.3-7.3-5.7-17.6-5.8-25.1-.2l-19.9,14.9v-12.1c0-12.2,9.9-22.1,22.1-22.1ZM52.1,384.1v-5.7l31.9-23.9c.2-.1.5-.3.8,0,12.8,9.9,38.8,26,75.5,28.6v1c0,29.8-24.3,54.1-54.1,54.1s-54.1-24.3-54.1-54.1ZM106.2,458.2c7.7,0,15.1-1.2,22.1-3.4v17.7c0,3.2.8,6.3,2.1,9.1l-23.8,17.8c-.2.2-.6.2-.8,0l-23.8-17.8c1.4-2.8,2.1-5.9,2.1-9.1v-17.7c7,2.2,14.4,3.4,22.1,3.4Z" />
    </svg>
  );
});

const QuestionBankIcon = memo(({ className }: IconProps) => {

  return (
    <svg
      className={className}
      viewBox="0 0 128 128">
      <path d="M35.3,81.4l1.6,2.1c.7.9,1.9,1,2.8.4.9-.7,1.1-1.9.4-2.8,0,0,0,0,0,0l-1.6-2.1c-.9-1.2-1.1-2.9-.5-4.2.6-2,.3-4.1-1-5.8-2.3-2.9-6.5-3.5-9.6-1.5-1.5,1-2.6,2.6-2.9,4.5-.3,1.7.1,3.5,1.2,4.9.7.9,1.9,1.1,2.8.4.9-.7,1.1-1.9.4-2.8-.4-.5-.6-1.2-.5-1.8.1-.7.6-1.4,1.2-1.8,1.3-.9,3.1-.7,4.1.6.5.6.6,1.4.4,2.2-1,2.6-.6,5.6,1.1,7.9Z" />
      <path d="M41.8,87.3c-.9.6-1.1,1.9-.5,2.8s1.9,1.1,2.8.5c.9-.6,1.1-1.9.5-2.8-.6-.9-1.9-1.1-2.8-.5Z" />
      <path d="M88.8,83.6s0,0,0,0h0c.9.7,2.1.5,2.8-.4l1.6-2.1c.9-1.2,2.2-1.8,4.3-1.9,2.1,0,4-1,5.3-2.7,1.1-1.4,1.5-3.2,1.2-4.9-.3-1.8-1.3-3.4-2.9-4.5-3.1-2.1-7.2-1.5-9.6,1.4-.7.9-.5,2.1.4,2.8.9.7,2.1.5,2.8-.4,1-1.2,2.8-1.5,4.1-.5.6.4,1.1,1.1,1.2,1.8.1.6,0,1.3-.5,1.8-.5.7-1.3,1.1-2.2,1.1-2.9,0-5.6,1.2-7.3,3.4l-1.6,2.1c-.7.9-.5,2.1.4,2.8Z" />
      <path d="M87.2,87.4c-.9-.6-2.1-.4-2.8.5s-.4,2.1.5,2.8,2.1.4,2.8-.5.4-2.1-.5-2.8Z" />
      <path d="M64,24c1.1,0,2-.9,2-2v-4c0-1.1-.9-2-2-2s-2,.9-2,2v4c0,1.1.9,2,2,2Z" />
      <path d="M50.3,62.8l.3.5c1.9,3,4.8,7.6,4.8,11.1v1.7c0,1.8,1.1,3.4,2.8,4v.4c0,1.9,1.6,3.5,3.5,3.4h5.6c1.9,0,3.5-1.5,3.5-3.4v-.4c1.7-.6,2.8-2.2,2.8-4,0-5.1,2.6-9.7,5.3-13.9,3.1-4.7,5.7-9.1,5.8-14.6,0-7.9-4.6-15-11.9-18.1-5.4-2.1-11.4-2.1-16.8,0-7.1,3-11.8,9.9-11.8,17.6,0,6.1,3.1,10.9,6,15.6ZM57.6,33.3c6.4-2.7,15.1-1.2,19.6,4.3,2.3,2.8,3.6,6.3,3.5,9.9,0,4.5-2.3,8.2-5.1,12.5-3.1,4.7-6,10-6,16.1,0,.2-.2.3-.4.3h-.4c-1.1,0-2,.9-2,2v1.5h-4.6v-1.5c0-1.1-.9-2-2-2h-.4c-.2,0-.4-.1-.4-.3v-1.7c0-4.7-3.2-9.9-5.4-13.3-2.7-4.3-5.7-8.8-5.7-14,0-6.1,3.7-11.6,9.3-13.9Z" />
      <path d="M52.6,24.5c.4,1,1.5,1.5,2.6,1.2,1-.4,1.6-1.5,1.2-2.6l-1.2-3.3c-.4-1-1.5-1.6-2.6-1.2-1,.4-1.6,1.5-1.2,2.6l1.2,3.3Z" />
      <path d="M44.1,30.1c.7.8,2,.9,2.8.2.8-.7.9-2,.2-2.8h0s-2.6-3-2.6-3c-.7-.8-2-.9-2.8-.2-.8.7-.9,2-.2,2.8l2.6,3Z" />
      <path d="M36.1,36.4s0,0,0,0l3.1,1.8c1,.5,2.2.2,2.7-.8.5-1,.2-2.2-.8-2.7,0,0,0,0,0,0l-3.1-1.8c-1-.5-2.2-.2-2.7.8-.5,1-.2,2.2.8,2.7Z" />
      <path d="M38.4,43.9s0,0,0,0l-4.8-1.5c-1.1-.3-2.2.2-2.5,1.3-.3,1.1.2,2.2,1.3,2.5,0,0,0,0,0,0l4.8,1.5c1.1.3,2.2-.2,2.5-1.3.3-1.1-.2-2.2-1.3-2.5Z" />
      <path d="M37.4,53.8l-3.5.6c-1.1.2-1.8,1.2-1.6,2.3,0,0,0,0,0,0,.2,1.1,1.2,1.8,2.3,1.6l3.5-.6c1.1-.2,1.8-1.2,1.6-2.3s-1.2-1.8-2.3-1.6Z" />
      <path d="M89.9,56.7l3.5.6c1.1.2,2.1-.5,2.3-1.6.2-1.1-.5-2.1-1.6-2.3,0,0,0,0,0,0l-3.5-.6c-1.1-.2-2.1.5-2.3,1.6-.2,1.1.5,2.1,1.6,2.3Z" />
      <path d="M88.3,45.4s0,0,0,0c.4,1,1.5,1.6,2.5,1.3l4.8-1.5c1.1-.3,1.6-1.5,1.3-2.5-.3-1.1-1.5-1.6-2.5-1.3,0,0,0,0,0,0l-4.8,1.5c-1.1.3-1.6,1.5-1.3,2.5Z" />
      <path d="M86.1,36.4h0c.6.9,1.8,1.3,2.7.8l3.1-1.8c1-.5,1.3-1.8.8-2.7-.5-1-1.8-1.3-2.7-.8l-3.1,1.8c-1,.5-1.3,1.8-.8,2.7Z" />
      <path d="M82.4,29.8c.4,0,.8-.1,1.2-.4l4.2-3c.9-.6,1.1-1.9.5-2.8s-1.9-1.1-2.8-.5l-4.2,3c-.9.7-1.1,1.9-.4,2.8.4.5,1,.8,1.6.8Z" />
      <path d="M73.8,25.7s0,0,0,0c1,.4,2.2-.2,2.6-1.2l1.2-3.3c.4-1-.1-2.2-1.2-2.6-1-.4-2.2.1-2.6,1.2,0,0,0,0,0,0l-1.2,3.3c-.4,1,.1,2.2,1.2,2.6Z" />
      <path d="M59.6,48.3c1.1,0,2-.9,2-2,.1-1.6,1.5-2.8,3.1-2.7,1.4.1,2.5,1.2,2.7,2.7,0,.8-.4,1.6-1.1,2.1-2.3,1.6-3.5,4.3-3.3,7.1v2.6c0,1.1.9,2,2,2,1.1,0,2-.9,2-2v-2.6c-.2-1.5.5-3,1.7-3.9,1.7-1.2,2.7-3.2,2.7-5.3-.1-3.8-3.3-6.8-7.1-6.7-3.6.1-6.5,3-6.7,6.7,0,1.1.9,2,2,2Z" />
      <circle
        cx="65"
        cy="66"
        r="2" />
      <path d="M126,100H2c-1.1,0-2,.9-2,2v4c0,3.3,2.7,6,6,6h116c3.3,0,6-2.7,6-6v-4c0-1.1-.9-2-2-2ZM124,106c0,1.1-.9,2-2,2H6c-1.1,0-2-.9-2-2v-2h120v2Z" />
      <path d="M10,35c0-1.1.9-2,2-2h19.3c.5-1.9,1.9-3.4,3.8-4H12c-3.3,0-6,2.7-6,6v61h4v-61Z" />
      <path d="M96.2,30.7c.4.7.6,1.5.7,2.3h19.1c1.1,0,2,.9,2,2v61h4v-61c0-3.3-2.7-6-6-6h-21.3c.6.5,1.1,1.1,1.5,1.7Z" />
    </svg>
  );
});

const StyledRecommendationsIcon = styled(RecommendationsIcon)(({ theme }) => ({
  color: theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main,
  fill: theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main,
  height: 60,
  width: 60,
}));
const StyledQuestionBankIcon = styled(QuestionBankIcon)(({ theme }) => ({
  color: theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main,
  fill: theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main,
  height: 70,
  width: 70,
  marginTop: -13,
  marginBottom: -6,
}));
const StyledSearchIcon = styled(SearchIcon)(({ theme }) => ({
  color: theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main,
  fill: theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main,
  height: 55,
  width: 55,
}));

const CardRoot = styled.div(({ theme }) => ({
  padding: 20,
  borderRadius: 8,
  backgroundColor: theme.palette.white.main,
  border: `2px solid ${theme.palette.gray.light4}`,
  cursor: 'pointer',

  width: 260,
  height: 305,

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',

  '&:hover': {
    backgroundColor: theme.isTrinityThemed ? theme.palette.sentiment.primary.light : theme.palette.btnPrimary.bg,
    border: `2px solid ${theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main}`,
  },
}));

const CardTitle = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 18,
  marginBottom: 12,
  marginTop: 10,
  height: 63,
}));

const CardCopy = styled.div({

});

const CardFooter = styled.div({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 15,
});

const CardLink = styled.div(({ theme }) => ({
  borderRadius: '50%',
  backgroundColor: theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main,
  padding: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
}));

const StyledChevronRightIcon = styled(ChevronRight)(({ theme }) => ({
  color: theme.palette.white.main,
  paddingLeft: 2,
}));